import { loadGridPrefs, saveGridPrefs } from 'components/grid/hiGrid/core/gridLocalPrefs';

export const PRINT_PAPER_OPTIONS = [
  { value: 'A4', label: 'A4' },
  { value: 'A3', label: 'A3' },
];

export const PRINT_ORIENT_OPTIONS = [
  { value: 'portrait', label: '세로' },
  { value: 'landscape', label: '가로' },
];

export const PRINT_FONT_OPTIONS = [
  { value: 'Pretendard', label: 'Pretendard' },
  { value: 'Noto Sans KR', label: 'Noto Sans KR' },
  { value: 'Malgun Gothic', label: '맑은 고딕' },
  { value: 'Batang', label: '바탕' },
];

export const PRINT_FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12];
export const PRINT_BAND_FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14];
export const PRINT_ALIGN_OPTIONS = [
  { value: 'left', label: '왼쪽' },
  { value: 'center', label: '가운데' },
  { value: 'right', label: '오른쪽' },
];

export const PRINT_COL_WIDTH_MIN = 8;
export const PRINT_COL_WIDTH_MAX = 80;
export const PRINT_ROWNO_FIELD = '__rowNo';
export const PRINT_ROWNO_WIDTH_DEFAULT = 12;

export const PRINT_PAPER_MM = {
  A4: { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } },
  A3: { portrait: { w: 297, h: 420 }, landscape: { w: 420, h: 297 } },
};

export function sheetSizeMm(layout) {
  const paper = layout?.paper === 'A3' ? 'A3' : 'A4';
  const orient = layout?.orientation === 'portrait' ? 'portrait' : 'landscape';
  return PRINT_PAPER_MM[paper][orient];
}

export function contentWidthMm(layout) {
  const sheet = sheetSizeMm(layout);
  const m = layout?.marginMm || defaultMargins();
  const left = Number(m.left);
  const right = Number(m.right);
  return Math.max(40, sheet.w - (Number.isFinite(left) ? left : 15) - (Number.isFinite(right) ? right : 15));
}

export function clampColMm(value, fallback = 20) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(PRINT_COL_WIDTH_MIN, Math.min(PRINT_COL_WIDTH_MAX, Math.round(n)));
}
export const PRINT_STAMP_COUNTS = [2, 3, 4, 5];
export const PRINT_STAMP_POSITIONS = [
  { value: 'titleRight', label: '제목 오른쪽' },
  { value: 'headerRight', label: '머릿글 오른쪽' },
];

export const PRINT_TOKENS = [
  { token: '{출력일}', label: '출력일' },
  { token: '{페이지}', label: '페이지' },
  { token: '{전체쪽}', label: '전체쪽' },
  { token: '{화면명}', label: '화면명' },
  { token: '{기관}', label: '기관' },
  { token: '{사용자}', label: '사용자' },
];

export const PRINT_STAMP_BUILTIN = {
  2: ['담당', '결재'],
  3: ['담당', '검토', '결재'],
  4: ['담당', '검토', '협조', '결재'],
  5: ['담당', '검토', '협조', '부서장', '결재'],
};

export function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function defaultBandCell(overrides = {}) {
  return {
    text: '',
    align: 'left',
    fontSizePt: 9,
    ...overrides,
  };
}

export function defaultHeader() {
  return {
    left: defaultBandCell({ align: 'left' }),
    center: defaultBandCell({ align: 'center' }),
    right: defaultBandCell({ align: 'right', text: '{출력일}' }),
  };
}

export function defaultFooter() {
  return {
    left: defaultBandCell({ align: 'left' }),
    center: defaultBandCell({ align: 'center', text: '{페이지} / {전체쪽}' }),
    right: defaultBandCell({ align: 'right' }),
  };
}

export function defaultStamp(cellCount = 3) {
  const n = PRINT_STAMP_COUNTS.includes(Number(cellCount)) ? Number(cellCount) : 3;
  return {
    enabled: false,
    position: 'titleRight',
    cellCount: n,
    titles: [...(PRINT_STAMP_BUILTIN[n] || PRINT_STAMP_BUILTIN[3])],
    presetId: `builtin-${n}`,
  };
}

export function defaultMargins() {
  return { top: 15, right: 15, bottom: 15, left: 15 };
}

export function defaultPrintLayout({ title = '', columnIds = [] } = {}) {
  return {
    title: String(title || ''),
    columnIds: [...columnIds],
    fontFamily: 'Pretendard',
    fontSizePt: 9,
    paper: 'A4',
    orientation: 'landscape',
    showRowNumber: true,
    marginMm: defaultMargins(),
    header: defaultHeader(),
    footer: defaultFooter(),
    stamp: defaultStamp(3),
    columnWidthsMm: {},
    rowNumberWidthMm: PRINT_ROWNO_WIDTH_DEFAULT,
  };
}

function clampMm(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(5, Math.min(40, Math.round(n)));
}

function normalizeBandCell(raw, fallback) {
  const base = defaultBandCell(fallback);
  if (!raw || typeof raw !== 'object') return base;
  const fontSize = Number(raw.fontSizePt);
  const align = PRINT_ALIGN_OPTIONS.some((o) => o.value === raw.align) ? raw.align : base.align;
  return {
    text: raw.text != null ? String(raw.text) : base.text,
    align,
    fontSizePt: PRINT_BAND_FONT_SIZE_OPTIONS.includes(fontSize) ? fontSize : base.fontSizePt,
  };
}

function normalizeBand(raw, fallback) {
  const fb = fallback || defaultHeader();
  return {
    left: normalizeBandCell(raw?.left, fb.left),
    center: normalizeBandCell(raw?.center, fb.center),
    right: normalizeBandCell(raw?.right, fb.right),
  };
}

function normalizeStamp(raw) {
  const base = defaultStamp(3);
  if (!raw || typeof raw !== 'object') return base;
  const cellCount = PRINT_STAMP_COUNTS.includes(Number(raw.cellCount))
    ? Number(raw.cellCount)
    : base.cellCount;
  const builtin = PRINT_STAMP_BUILTIN[cellCount] || PRINT_STAMP_BUILTIN[3];
  const titles = Array.isArray(raw.titles)
    ? raw.titles.map((t) => String(t || '').trim())
    : [...builtin];
  while (titles.length < cellCount) titles.push(builtin[titles.length] || '');
  return {
    enabled: raw.enabled === true,
    position: raw.position === 'headerRight' ? 'headerRight' : 'titleRight',
    cellCount,
    titles: titles.slice(0, cellCount),
    presetId: raw.presetId ? String(raw.presetId) : `builtin-${cellCount}`,
  };
}

function normalizeColumnWidths(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  Object.entries(raw).forEach(([k, v]) => {
    const id = String(k || '').trim();
    if (!id || id === PRINT_ROWNO_FIELD) return;
    out[id] = clampColMm(v, 20);
  });
  return out;
}

function migrateLegacy(raw, base) {
  if (!raw || raw.header || raw.footer || raw.stamp) return raw;
  const next = { ...raw };
  if (raw.headerLeft != null || raw.headerRight != null) {
    next.header = {
      left: defaultBandCell({ align: 'left', text: String(raw.headerLeft || '') }),
      center: defaultBandCell({ align: 'center' }),
      right: defaultBandCell({ align: 'right', text: String(raw.headerRight || '') }),
    };
  }
  if (raw.footer != null && String(raw.footer).trim()) {
    next.footer = {
      left: defaultBandCell({ align: 'left' }),
      center: defaultBandCell({ align: 'center', text: String(raw.footer) }),
      right: defaultBandCell({ align: 'right' }),
    };
  }
  return next;
}

export function normalizePrintLayout(raw, { title = '', columnIds = [] } = {}) {
  const base = defaultPrintLayout({ title, columnIds });
  const src = migrateLegacy(raw, base);
  if (!src || typeof src !== 'object') return base;
  const ids = Array.isArray(src.columnIds)
    ? src.columnIds.map((id) => String(id)).filter(Boolean)
    : columnIds;
  const fontSize = Number(src.fontSizePt);
  const m = src.marginMm || {};
  const fb = defaultMargins();
  return {
    title: src.title != null ? String(src.title) : base.title,
    columnIds: ids.length ? ids : columnIds,
    fontFamily: PRINT_FONT_OPTIONS.some((f) => f.value === src.fontFamily)
      ? src.fontFamily
      : base.fontFamily,
    fontSizePt: PRINT_FONT_SIZE_OPTIONS.includes(fontSize) ? fontSize : base.fontSizePt,
    paper: src.paper === 'A3' ? 'A3' : 'A4',
    orientation: src.orientation === 'portrait' ? 'portrait' : 'landscape',
    showRowNumber: src.showRowNumber !== false,
    marginMm: {
      top: clampMm(m.top, fb.top),
      right: clampMm(m.right, fb.right),
      bottom: clampMm(m.bottom, fb.bottom),
      left: clampMm(m.left, fb.left),
    },
    header: normalizeBand(src.header, base.header),
    footer: normalizeBand(src.footer, base.footer),
    stamp: normalizeStamp(src.stamp),
    columnWidthsMm: normalizeColumnWidths(src.columnWidthsMm),
    rowNumberWidthMm: clampColMm(src.rowNumberWidthMm, PRINT_ROWNO_WIDTH_DEFAULT),
  };
}

export function loadPrintLayout(gridId, prefsUserId, defaults) {
  const prefs = loadGridPrefs(gridId, prefsUserId);
  return normalizePrintLayout(prefs.printLayout, defaults);
}

export function savePrintLayout(gridId, prefsUserId, layout) {
  if (!gridId) return;
  saveGridPrefs(gridId, prefsUserId, {
    printLayout: normalizePrintLayout(layout, {
      title: layout?.title,
      columnIds: layout?.columnIds || [],
    }),
  });
}

export function applyPrintTokens(text, ctx = {}) {
  let s = String(text || '');
  const map = {
    '{출력일}': ctx.today || todayYmd(),
    '{페이지}': String(ctx.page ?? 1),
    '{전체쪽}': String(ctx.pageCount ?? 1),
    '{화면명}': ctx.screenName || '',
    '{기관}': ctx.tenantName || ctx.tenantCode || '',
    '{사용자}': ctx.userName || '',
  };
  Object.entries(map).forEach(([token, value]) => {
    s = s.split(token).join(value);
  });
  return s;
}
